import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import './i18n/config'; // Initialize i18next

// Layout components
import Layout from './components/Layout/Layout';
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';

// Page components
import Dashboard from './pages/Dashboard';
import Assets from './pages/Assets';
import AssetEdit from './pages/AssetEdit';
import AssetActivity from './pages/AssetActivity';
import UserActivity from './pages/UserActivity';
import Folders from './pages/Folders';
import Encrypt from './pages/Encrypt';
import Support from './pages/Support';
import Settings from './pages/Settings';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import RegisterVerifyOtp from './pages/Auth/RegisterVerifyOtp';
import ForgotPassword from './pages/Auth/ForgotPassword';
import AcceptInvite from './pages/Auth/AcceptInvite';
import Handoff from './pages/Auth/Handoff';
import UserProfile from './components/User/UserProfile';
import Companies from './pages/Companies';
import CompanyForm from './pages/CompanyForm';
import Policies from './pages/Policies';
import PolicyCreate from './pages/PolicyCreate';
import PolicyEdit from './pages/PolicyEdit';
import PolicyView from './pages/PolicyView';
import ContactsGroups from './pages/ContactsGroups';
import Users from './pages/Users';
import UserDetails from './pages/UserDetails';
import Notifications from './pages/Notifications';
import EmailTemplates from './pages/EmailTemplates';
import Devices from './pages/Devices';
import DeviceDetails from './pages/DeviceDetails';
import Infrastructure from './pages/Infrastructure';
import AdminWatermarkSettings from './pages/AdminWatermarkSettings';
import AdminPolicySettings from './pages/AdminPolicySettings';
import AdminPasswordSettings from './pages/AdminPasswordSettings';
import AdminNotifications from './pages/AdminNotifications';
import AdminAssetProtectionSettings from './pages/AdminAssetProtectionSettings';
import AdministrationLicenses from './pages/AdministrationLicenses';
import AdministrationSubscriptions from './pages/AdministrationSubscriptions';
import AdministrationSubscriptionDetail from './pages/AdministrationSubscriptionDetail';
import AdministrationPlatformDefaults from './pages/AdministrationPlatformDefaults';
import AdministrationUsages from './pages/AdministrationUsages';
import InviteHistory from './pages/InviteHistory';
import Invitations from './pages/Invitations';
import InviteDetails from './pages/InviteDetails';
import Translations from './pages/Translations';
import TranslationDetail from './pages/TranslationDetail';
import Downloads from './pages/Downloads';
import Audits from './pages/Audits';

// Context and hooks
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { UserProvider, useUser } from './contexts/UserContext';
import { PageTitleProvider } from './contexts/PageTitleContext';
import { UnsavedChangesProvider } from './contexts/UnsavedChangesContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { UserPreferencesProvider } from './contexts/UserPreferencesContext';
import { PolicyViewHeaderProvider } from './contexts/PolicyViewHeaderContext';
import { useAssetsTabCleanup } from './hooks/useAssetsTabCleanup';
import { useSettingsTabCleanup } from './hooks/useSettingsTabCleanup';
import { useProfileTabCleanup } from './hooks/useProfileTabCleanup';
import { useAdminPolicySettingsTabCleanup } from './hooks/useAdminPolicySettingsTabCleanup';
import { usePlatformDefaultsTabCleanup } from './hooks/usePlatformDefaultsTabCleanup';
import DownloadOnboarding from './components/DownloadOnboarding/DownloadOnboarding';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 0, // Always consider data stale
      gcTime: 0, // Don't cache data by default (gcTime replaces cacheTime)
    },
  },
});


// Reader users land on Assets > Shared with me instead of Dashboard; auditors land on Logs
const HomePage: React.FC = () => {
  const { user } = useAuth();
  if (user?.role === 'company_auditor') return <Navigate to="/logs" replace />;
  if (user?.role === 'system_auditor') return <Navigate to="/administration/logs" replace />;
  if (user?.mode === 'reader') {
    return <Navigate to="/assets?tab=shared-with-me" replace />;
  }
  return <Dashboard />;
};

// Hides the Settings page from reader-mode end-users (full-mode only
// surface). Belt-and-suspenders to the Sidebar / Header gates — a
// reader who hand-types `/settings` or follows a stale link bounces
// to `/`, which routes them through `HomePage` and onto their
// Assets > Shared-with-me landing. Admin-in-reader is still allowed
// (matches the Sidebar / Header rule).
const SettingsGate: React.FC = () => {
  const { user } = useAuth();
  if (user?.mode === 'reader'
      && user?.role !== 'admin'
      && user?.role !== 'sysadmin') {
    return <Navigate to="/" replace />;
  }
  return <Settings />;
};

// Main app routes
const AppRoutes: React.FC = () => {
  const { user, isAuthenticated, isLoading, refreshUserData } = useAuth();
  const { devices, isLoading: userLoading } = useUser();
  const location = useLocation();

  const hasDesktopDevice = (devices || []).some((d: { type?: string }) => d.type === 'desktop');
  const showDownloadsFirst = !userLoading && !hasDesktopDevice;
  const showOnboarding = Boolean(user && user.is_onboarded === false);

  // Clear tab states when navigating away from respective routes
  useAssetsTabCleanup();
  useSettingsTabCleanup();
  useProfileTabCleanup();
  useAdminPolicySettingsTabCleanup();
  usePlatformDefaultsTabCleanup();

  // Debug logging for navigation
  useEffect(() => {
    console.log('🔍 APP ROUTES DEBUG: Location changed');
    console.log('  - pathname:', location.pathname);
    console.log('  - search:', location.search);
    console.log('  - hash:', location.hash);
  }, [location]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-governate-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/register/verify-email" element={<RegisterVerifyOtp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/auth/handoff" element={<Handoff />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Auditors only have access to Logs (company or admin) and Profile; redirect any other path
  const isAuditor = user?.role === 'company_auditor' || user?.role === 'system_auditor';
  const auditorAllowedPaths =
    user?.role === 'system_auditor' ? ['/profile', '/administration/logs'] : ['/profile', '/logs'];
  if (isAuditor && !auditorAllowedPaths.includes(location.pathname)) {
    return (
      <Navigate to={user?.role === 'system_auditor' ? '/administration/logs' : '/logs'} replace />
    );
  }

  return (
    <PageTitleProvider>
      <OnboardingProvider>
        <PolicyViewHeaderProvider>
        <Layout>
          <Sidebar />
          <div className="flex-1 flex flex-col min-w-0 h-screen">
            <Header />
            <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-black p-2 md:p-6">
              <DownloadOnboarding
                showOnboarding={showOnboarding}
                showNoDesktop={showDownloadsFirst}
                pathname={location.pathname}
                onOnboardingComplete={refreshUserData}
              />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/downloads" element={<Downloads />} />
              <Route path="/assets" element={<Assets />} />
              <Route path="/assets/:id/manage" element={<AssetEdit />} />
              <Route path="/assets/:id/activity" element={<AssetActivity />} />
              <Route path="/users/:id/activity" element={<UserActivity />} />
              <Route path="/folders" element={<Folders />} />
              <Route path="/folders/device/:deviceId" element={<Folders />} />
              <Route path="/policies" element={<Policies />} />
              <Route path="/policies/new" element={<PolicyCreate />} />
              <Route path="/policies/create" element={<PolicyCreate />} />
              <Route path="/policies/:id" element={<PolicyView />} />
              <Route path="/policies/:id/edit" element={<PolicyEdit />} />
              <Route path="/encrypt" element={<Encrypt />} />
              <Route path="/support" element={<Support />} />
              <Route path="/settings" element={<SettingsGate />} />
              <Route path="/profile" element={<UserProfile />} />
              <Route path="/clients" element={<Companies />} />
              <Route path="/clients/new" element={<CompanyForm />} />
              <Route path="/clients/:id" element={<CompanyForm />} />
              <Route path="/contacts" element={<ContactsGroups />} />
              <Route path="/users" element={<Users />} />
              <Route path="/users/:id" element={<UserDetails />} />
              <Route path="/devices" element={<Devices />} />
              <Route path="/devices/:id" element={<DeviceDetails />} />
              <Route path="/administration/watermarks" element={<AdminWatermarkSettings />} />
              <Route path="/administration/policy" element={<AdminPolicySettings />} />
              <Route path="/administration/passwords" element={<AdminPasswordSettings />} />
              <Route path="/administration/notifications" element={<AdminNotifications />} />
              <Route path="/administration/licenses" element={<AdministrationLicenses />} />
              <Route path="/administration/subscriptions" element={<AdministrationSubscriptions />} />
              <Route path="/administration/subscriptions/:id" element={<AdministrationSubscriptionDetail />} />
              <Route path="/administration/platform-defaults" element={<AdministrationPlatformDefaults />} />
              <Route path="/administration/usages" element={<AdministrationUsages />} />
              <Route path="/administration/usages/clients" element={<AdministrationUsages />} />
              <Route path="/administration/usages/clients/:companyId" element={<AdministrationUsages />} />
              <Route path="/administration/usages/personal" element={<AdministrationUsages />} />
              <Route path="/administration/usages/clients/:companyId/invites/:userId" element={<InviteHistory />} />
              <Route path="/invitations" element={<Invitations />} />
              <Route path="/invitations/:id" element={<InviteDetails />} />
              <Route path="/administration/asset-protection" element={<AdminAssetProtectionSettings />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/email-templates" element={<EmailTemplates />} />
              <Route path="/email-templates/:id" element={<EmailTemplates />} />
              <Route path="/infrastructure" element={<Infrastructure />} />
              {/* Resources: hidden for now; sidebar and backend routes commented out. Re-enable when needed. */}
              {/* <Route path="/resources" element={<Infrastructure />} /> */}
              <Route path="/translations" element={<Translations />} />
              <Route path="/translations/:langId" element={<TranslationDetail />} />
              <Route path="/logs" element={<Audits />} />
              <Route path="/administration/logs" element={<Audits systemScope={true} />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </main>
          </div>
        </Layout>
        </PolicyViewHeaderProvider>
      </OnboardingProvider>
    </PageTitleProvider>
  );
};

// Main App component
const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserProvider>
          <UserPreferencesProvider>
            <NotificationsProvider>
              <UnsavedChangesProvider>
                <Router>
                  <div className="App">
                    <AppRoutes />
                    <Toaster
                      position="top-right"
                      toastOptions={{
                        duration: 4000,
                        style: {
                          background: '#363636',
                          color: '#fff',
                        },
                        success: {
                          duration: 3000,
                          iconTheme: {
                            primary: '#22c55e',
                            secondary: '#fff',
                          },
                        },
                        error: {
                          duration: 5000,
                          iconTheme: {
                            primary: '#ef4444',
                            secondary: '#fff',
                          },
                        },
                      }}
                    />
                  </div>
                </Router>
              </UnsavedChangesProvider>
            </NotificationsProvider>
          </UserPreferencesProvider>
        </UserProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
